import { Device, RadarConfiguration, Vector2, Vector3 } from "../../models";
import { mat4, vec3, vec4 } from 'gl-matrix';
import { Matrix, SingularValueDecomposition } from 'ml-matrix';
import { HomographyHelper } from "./homography";
import { iPointTranslator } from "./iPointTranslator";

type CalibrationParameters = {
    pitch: number;      // degrees
    roll: number;       // degrees
    height: number;     // camera height
    returnHeight: number; // estimated return height
};

/** One user-clicked correspondence: an image pixel (640x480 basis) and where it really is on the ground (device frame, meters, z=0). */
export type GroundCalibrationPair = { pixel: Vector2; ground: Vector3 };

export type PoseFitOptions = {
    cameraModel?: CameraStreamModel;
    estimatedReturnHeight?: number;
    /** Let the fit adjust the device heading (soft prior toward no change). Default true. */
    fitHeading?: boolean;
    /**
     * Let the fit move the device position (soft prior toward no move). Default false: a
     * handful of noisy map clicks constrains position weakly — it trades against pitch/height
     * along the view axis — and an unlocked position can wander meters to shave a few pixels.
     * Enable only with 4+ well-spread pairs when the map pin itself is in doubt.
     */
    fitPosition?: boolean;
    /**
     * Let the fit scale fx/fy (soft prior toward 1.0). Default false. Diagnostic for dewarped
     * streams, where the effective focal length depends on the unit's actual dewarp config
     * (the repo file is a template): a scale that repeatedly lands away from 1.0 means the
     * camera-model constants should be updated to scale × current focal. Azimuth-spread pairs
     * are what make this observable.
     */
    fitFocalScale?: boolean;
    /**
     * Apply the soft anti-runaway priors on heading/position/focal (default true). Set false
     * for exact registration: pure (Huber) reprojection least-squares with every freed
     * parameter unbiased. Use with 4+ well-spread pairs and trustworthy clicks — the priors
     * exist to keep noisy few-point fits from wandering, and without them the data alone
     * decides.
     */
    regularize?: boolean;
};

/**
 * Convert a fit's device-frame ground offset + heading offset into the corrected facility
 * placement. Inverse of facilityToDevice's placement terms: dev = rotate(flipY(p - pos), heading),
 * so deltaPosition = -flipY(rotate(groundOffset, -(baseHeading + headingOffset))).
 */
export const placementFromPoseFit = (
    fit: { headingOffsetDeg: number; groundOffsetX: number; groundOffsetY: number },
    base: { positionX: number; positionY: number; headingDegrees: number },
): { positionX: number; positionY: number; headingDegrees: number } => {
    const newHeading = base.headingDegrees + fit.headingOffsetDeg;
    const rad = (-newHeading * Math.PI) / 180;
    const c = Math.cos(rad);
    const s = Math.sin(rad);
    const tX = fit.groundOffsetX * c - fit.groundOffsetY * s;
    const tY = fit.groundOffsetX * s + fit.groundOffsetY * c;
    return {
        positionX: base.positionX - tX,
        positionY: base.positionY + tY,
        headingDegrees: ((newHeading % 360) + 360) % 360,
    };
};

export const HEIGHT_FIT_TOLERANCE = 0.15;

/**
 * Compare a fitted camera height against the height on record.
 *
 * Height is the one pose value a person can measure directly, and every ground coordinate the
 * camera produces scales with it. A fit whose points all sit at a similar range can trade height
 * against pitch and still reproject well, so a low pixel error alone doesn't prove the scale is
 * right — a large disagreement with the measured height does prove something is wrong.
 *
 * Returns null when there is nothing to compare or the two agree within tolerance.
 */
export const heightFitMismatch = (fittedM: number | null | undefined, measuredM: number | null | undefined, tolerance = HEIGHT_FIT_TOLERANCE): { fitted: number; measured: number; percent: number } | null => {
    if (!fittedM || !measuredM || fittedM <= 0 || measuredM <= 0) return null;
    const off = Math.abs(fittedM - measuredM) / measuredM;
    if (off <= tolerance) return null;
    return { fitted: fittedM, measured: measuredM, percent: Math.round(off * 100) };
};

export type PoseFitResult = {
    /** Positive = looking down (the calculateFromParameters convention). */
    pitchDeg: number;
    rollDeg: number;
    /**
     * Degrees to ADD to the device heading that was used to build the pairs — corrects a
     * mis-placed map heading. NOT Device.fusionOffsetYaw: that field is the radar↔camera
     * offset fitted from radar returns, which this map-based tool cannot observe.
     */
    headingOffsetDeg: number;
    /**
     * Translation added to the device-frame ground points (meters), absorbing a mis-placed
     * device position. The caller converts it to a facility-frame position correction:
     * with H = pairHeading + headingOffsetDeg, deltaFacility = -flipY(rotate(offset, -H)).
     */
    groundOffsetX: number;
    groundOffsetY: number;
    heightM: number;
    /** Multiplier on the camera model's fx/fy (1 unless options.fitFocalScale). */
    focalScale: number;
    /** Mean reprojection error over the pairs, in 640x480-basis pixels. */
    meanErrorPx: number;
    /** Reprojection error of each input pair, same order, in pixels. */
    perPointErrorPx: number[];
};

/**
 * Radius where the radial distortion polynomial stops being trustworthy: the 2716N fit FOLDS at
 * r≈1.38 (larger field angles would map to smaller distorted radii — non-physical), so frame
 * corners have no preimage under the raw polynomial and iterative undistortion used to diverge
 * there. Beyond this radius both projection directions switch to a linear continuation of the
 * curve (slope taken at this radius), keeping the mapping monotonic, invertible, and continuous.
 * Inside this radius nothing changes. Must match IntrinsicsTranslatorV2.RadialExtensionStart —
 * the parity tests pin both sides. Corner accuracy is an extrapolation; only a corner-covering
 * recalibration can make it exact.
 */
const RADIAL_EXTENSION_START = 1.2;

type RadialExtension = { k1: number; k2: number; k3: number; startRd: number; slope: number };
let cachedRadialExtension: RadialExtension | undefined;
const getRadialExtension = (d: { k1: number; k2: number; k3: number }): RadialExtension => {
    if (cachedRadialExtension && cachedRadialExtension.k1 === d.k1 && cachedRadialExtension.k2 === d.k2 && cachedRadialExtension.k3 === d.k3) {
        return cachedRadialExtension;
    }
    const r = RADIAL_EXTENSION_START;
    const r2 = r * r;
    const r4 = r2 * r2;
    const r6 = r4 * r2;
    cachedRadialExtension = {
        k1: d.k1,
        k2: d.k2,
        k3: d.k3,
        startRd: r * (1 + d.k1 * r2 + d.k2 * r4 + d.k3 * r6),
        slope: 1 + 3 * d.k1 * r2 + 5 * d.k2 * r4 + 7 * d.k3 * r6,
    };
    return cachedRadialExtension;
};

/** Radial distortion scale r_d/r_u, using the linear extension beyond RADIAL_EXTENSION_START. */
const radialScale = (rU: number, d: { k1: number; k2: number; k3: number }): number => {
    if (rU <= RADIAL_EXTENSION_START) {
        const r2 = rU * rU;
        const r4 = r2 * r2;
        const r6 = r4 * r2;
        return 1 + d.k1 * r2 + d.k2 * r4 + d.k3 * r6;
    }
    const ext = getRadialExtension(d);
    return (ext.startRd + ext.slope * (rU - RADIAL_EXTENSION_START)) / rU;
};

/**
 * Which stream the projected pixels come from. Selects the pinhole intrinsics and whether the
 * 2716N distortion model applies:
 * - "raw2716N": the sensor's native stream — the OpenCV 2716N calibration + barrel distortion.
 * - "dewarped2716N" / "dewarped2701L": Orin devices run nvdewarper at the head of the camera
 *   pipeline, so every downstream frame (VA, snapshots, streams) is already an undistorted
 *   perspective image with its principal point at the frame center. Projecting those with the
 *   raw model undistorts an already-undistorted image and uses a cy that is 47.7 px off, which
 *   read as ground ranges up to ~40% short below the horizon.
 */
export type CameraStreamModel = "raw2716N" | "dewarped2716N" | "dewarped2701L";

type PinholeParams = { fx: number; fy: number; cx: number; cy: number };

// All at the 1920x1080 stream resolution.
const PINHOLE_BY_MODEL: Record<CameraStreamModel, PinholeParams> = {
    raw2716N: { fx: 1091.48385, fy: 1094.62827, cx: 971.484941, cy: 492.295 },
    // From MegaRadarFirmware DewarpParams-2716N.txt (also shipped verbatim as the
    // os-dewarp-2716N apt package): fisheye -> perspective with dst-focal-length
    // 1086.034/1088.452 on a 2100x1182 surface, output 1920x1080. Although the Gst-nvdewarper
    // docs describe scaling surfaces to the output (which would rescale the focal by 1920/2100
    // to ~993), point-pair lens-scale fits against ground truth on hardware land at ~1.09-1.11
    // ≈ 2100/1920 above that — i.e., the dst focal carries through to the output UNSCALED, so
    // it is used 1:1 here. Principal point sits at the frame center.
    dewarped2716N: { fx: 1086.0340252621618, fy: 1088.4517367277715, cx: 960, cy: 540 },
    // From DewarpParams-2701L.txt: dewarp surface == 1920x1080 output, focals pass through.
    dewarped2701L: { fx: 1122.52, fy: 1031.32, cx: 960, cy: 540 },
};

export class IntrinsicsTranslator implements iPointTranslator {
    private intrinsics!: mat4;
    private invertedIntrinsics!: mat4;
    private cameraModel: CameraStreamModel = "raw2716N";
    private focalScale: number = 1;
    private rotation!: mat4;
    private pitchRotation!: mat4;
    private rollRotation!: mat4;
    private invRotation!: mat4;
    private height: number = 0;
    private estimatedReturnHeight: number = 0;
    private is2dMode: boolean = true;
    private homography?: HomographyHelper;
    private planeNormal?: vec3;
    private planePoint?: vec3;

    calculateFromSettings(settings: Device | null, pitchDeg: number, rollDeg: number, estimatedReturnHeight: number): boolean {
        var mode2d = true;
        if ((settings?.sensorConfig?.$type ?? '').indexOf('RadarConfiguration') > 0) {
            console.log("Sensor config is a RadarConfiguration, setting 2D mode based on chirpConfig");
            mode2d = ((settings?.sensorConfig as RadarConfiguration)?.chirpConfig?.indexOf('channelCfg 15 7 0') ?? -1) < 0;
        }

        return this.calculateFromParameters(mode2d, settings?.height ?? 5, pitchDeg, rollDeg, estimatedReturnHeight);
    }

    fitPlane(points: vec3[]): { normal: vec3, pointOnPlane: vec3 } {
        if (points.length < 3) {
            throw new Error("Need at least 3 points to fit a planes");
        }

        const centroid = vec3.create();
        for (const p of points) {
            vec3.add(centroid, centroid, p);
        }
        vec3.scale(centroid, centroid, 1 / points.length);

        //center the data
        const centeredPoints = points.map(p => {
            const centered = vec3.create();
            vec3.subtract(centered, p, centroid);
            return centered;
        });

        //each row is a point: [x, y, z]
        const data = centeredPoints.map(p => [p[0], p[1], p[2]]);
        const dataMatrix = new Matrix(data);

        const svd = new SingularValueDecomposition(dataMatrix, { autoTranspose: true });

        //The normal is the right singular vector corresponding to the smallest singular value
        const V = svd.rightSingularVectors;
        const normalVec = V.getColumnVector(2); //smallest singular value is the last column

        const normal: vec3 = vec3.fromValues(
            normalVec.get(0, 0),
            normalVec.get(1, 0),
            normalVec.get(2, 0)
        );
        vec3.normalize(normal, normal);

        return {
            normal,
            pointOnPlane: centroid
        };
    }

    calculateFromParameters(is2d: boolean, height: number, pitchDeg: number, rollDeg: number, estimatedReturnHeight: number, cameraModel?: CameraStreamModel): boolean {

        var orientation = { pitch: pitchDeg, roll: rollDeg };
        this.is2dMode = is2d;
        this.height = height;
        this.estimatedReturnHeight = estimatedReturnHeight;
        if (cameraModel) this.cameraModel = cameraModel;
        this.focalScale = 1;

        var rotY: mat4 = mat4.fromYRotation(mat4.create(), orientation.roll * Math.PI / 180);
        var rotX = mat4.fromXRotation(mat4.create(), orientation.pitch * Math.PI / 180);
        this.rotation = mat4.multiply(mat4.create(), rotY, rotX);

        this.rollRotation = mat4.fromYRotation(mat4.create(), orientation.roll * Math.PI / 180);
        this.pitchRotation = mat4.fromXRotation(mat4.create(), orientation.pitch * Math.PI / 180);

        this.invRotation = mat4.invert(mat4.create(), this.rotation);

        this.intrinsics = this.buildIntrinsicsMatrix();
        this.invertedIntrinsics = mat4.invert(mat4.create(), this.intrinsics);

        return true;
    }

    estimateElevation(x: number, y: number): number {
        if (!this.planeNormal || !this.planePoint) return 0;
        const A = this.planeNormal[0], B = this.planeNormal[1], C = this.planeNormal[2];
        const x0 = this.planePoint[0], y0 = this.planePoint[1], z0 = this.planePoint[2];

        if (Math.abs(C) < 1e-6) throw new Error("Plane is vertical; can't solve for Z.");

        const z = z0 - (A * (x - x0) + B * (y - y0)) / C;
        return z;
    }

    //Takes pitch, roll, UVX, UVY, RadarX, Radar Y (range)
    estimateElevationFromRadarCamera(pitch: number, roll: number, data: [number, number, number, number]): number | null {
        //UV Points from the VA are distorted when we get them, TODO, account for if the device has a dewarper installed.
        const uv = this.standardUndistortPoint(new Vector2({ x: data[0], y: data[1] }));
        const u = uv.x;
        const v = uv.y;

        const xRadar = data[2];
        const zRadar = data[3];

        //Scale up to intrisnic size, VA is 640x480
        const pixelVec = vec3.fromValues(u / 640 * 1920, v / 480 * 1080, 1);

        //Translate to world coordinates using the inverted intrinsics
        const dirCamera = vec3.create();
        vec3.transformMat4(dirCamera, pixelVec, this.invertedIntrinsics);
        // This method's world frame is y-up; negate the y-down camera component rather than
        // flipping v around the image height (cy is off-center — see imageToGround).
        dirCamera[1] = -dirCamera[1];
        vec3.normalize(dirCamera, dirCamera);

        const pitchRad = (pitch) * Math.PI / 180;
        const rollRad = (roll) * Math.PI / 180;

        const rPitch = mat4.fromXRotation(mat4.create(), pitchRad);
        const rRoll = mat4.fromZRotation(mat4.create(), rollRad);

        var R_rotate = mat4.multiply(mat4.create(), rRoll, rPitch);

        const dirWorld = vec3.transformMat4(vec3.create(), dirCamera, R_rotate);
        vec3.normalize(dirWorld, dirWorld);

        //Origin for reference frame of returned coordinate
        const origin = vec3.fromValues(0, 0, 0);

        //Project the ray in the range direction if we can
        let t: number | null = null;

        if (Math.abs(dirWorld[2]) > 0.01) {
            t = (zRadar - origin[2]) / dirWorld[2];
        } else if (Math.abs(dirWorld[0]) > 0.01) {
            t = (xRadar - origin[0]) / dirWorld[0];
        } else {
            return null; //No valid intersection (parallel?)
        }

        const point = vec3.create();
        vec3.scaleAndAdd(point, origin, dirWorld, t);

        const yWorld = point[1]; //This projected point is the elevation (y)

        return yWorld;
    }

    findBestHeight(testData: number[][]): number {
        var tolerance = 0.05;
        var maxIterations = 50;
        var estimatedHeight = 5;
        var step = 1.0;
        var originalHeight = this.height;

        const evaluate = (estimatedHeight: number): number => {
            this.height = estimatedHeight;
            var totalDistance = 0;
            for (let i = 0; i < testData.length; i++) {
                const groundFromImage = this.imageToGround(new Vector2({ x: testData[i][0], y: testData[i][1] }));
                const groundFromRadar = this.radarToGround(new Vector3({ x: testData[i][2], y: testData[i][3], z: 0 }));
                var a = vec3.fromValues(groundFromRadar.x, groundFromRadar.y, 0);
                var b = vec3.fromValues(groundFromImage.x, groundFromImage.y, 0);
                var dist = vec3.distance(a, b);
                totalDistance += dist;
            }
            var averageDistance = totalDistance / testData.length;
            return averageDistance;
        };

        for (let iter = 0; iter < maxIterations; iter++) {
            let improved = false;
            let directions = [
                [estimatedHeight + step],
                [estimatedHeight - step]
            ];

            let currentDistance = evaluate(estimatedHeight);
            for (const [testHeight] of directions) {
                const testDistance = evaluate(testHeight);
                if (testDistance < currentDistance) {
                    estimatedHeight = testHeight;
                    improved = true;
                    break;
                }
            }
            if (!improved) {
                if (step <= tolerance) break;
                step /= 2;
            }
        }

        this.height = originalHeight;

        return estimatedHeight;
    }

    findBestPitchRollForDistance(testData: number[][]): { pitch: number, roll: number } {
        var pitch = 20;
        var roll = 0;
        var step = 1.0;
        var tolerance = 0.05;
        var maxIterations = 50;
        var originalRotation = this.rotation;
        var originalInverseRotation = this.invRotation;

        const evaluate = (p: number, r: number): number => {
            var rotY: mat4 = mat4.fromYRotation(mat4.create(), r * Math.PI / 180);
            var rotX = mat4.fromXRotation(mat4.create(), p * Math.PI / 180);
            this.rotation = mat4.multiply(mat4.create(), rotY, rotX);

            this.rollRotation = mat4.fromYRotation(mat4.create(), r * Math.PI / 180);
            this.pitchRotation = mat4.fromXRotation(mat4.create(), p * Math.PI / 180);
            this.invRotation = mat4.invert(mat4.create(), this.rotation);

            var totalDistance = 0;
            for (let i = 0; i < testData.length; i++) {
                const groundFromImage = this.imageToGround(new Vector2({ x: testData[i][0], y: testData[i][1] }));
                const groundFromRadar = this.radarToGround(new Vector3({ x: testData[i][2], y: testData[i][3], z: 0 }));
                var a = vec3.fromValues(groundFromRadar.x, groundFromRadar.y, 0);
                var b = vec3.fromValues(groundFromImage.x, groundFromImage.y, 0);
                var dist = vec3.distance(a, b);
                totalDistance += dist;
            }
            var averageDistance = totalDistance / testData.length;
            return averageDistance;
        };

        for (let iter = 0; iter < maxIterations; iter++) {
            let improved = false;
            let directions = [
                [pitch + step, roll],
                [pitch - step, roll],
                [pitch, roll + step],
                [pitch, roll - step],
                [pitch + step, roll + step],
                [pitch + step, roll - step],
                [pitch - step, roll + step],
                [pitch - step, roll - step],
            ];

            let currentDistance = evaluate(pitch, roll);
            for (const [p, r] of directions) {
                const testDistance = evaluate(p, r);
                if (testDistance < currentDistance) {
                    pitch = p;
                    roll = r;
                    improved = true;
                    break; // greedy move
                }
            }

            if (!improved) {
                if (step <= tolerance) break; // Converged
                step /= 2; // Refine search granularity
            }
        }

        this.rotation = originalRotation;
        this.invRotation = originalInverseRotation;

        return { pitch, roll };
    }

    findBestPitchRollForPlane(testData: number[][]): { pitch: number, roll: number, bestFit: { normal: vec3, pointOnPlane: vec3 } } {
        var pitch = 20;
        var roll = 0;
        var bestFit = { normal: vec3.fromValues(0, 0, 0), pointOnPlane: vec3.fromValues(0, 0, 0) };
        var step = 1.0;
        var tolerance = 0.05;
        var maxIterations = 50;

        const evaluate = (p: number, r: number): { normal: vec3, pointOnPlane: vec3 } => {
            const points: vec3[] = [];
            for (let i = 0; i < testData.length; i++) {
                const elevation = this.estimateElevationFromRadarCamera(p, r, [testData[i][0], testData[i][1], testData[i][2], testData[i][3]]);
                if (!elevation)
                    return { normal: vec3.fromValues(0, 0, 0), pointOnPlane: vec3.fromValues(0, 0, 0) };

                points.push(vec3.fromValues(testData[i][2], testData[i][3], elevation));
            }

            var planeFit = this.fitPlane(points);
            if (!planeFit)
                return { normal: vec3.fromValues(0, 0, 0), pointOnPlane: vec3.fromValues(0, 0, 0) };
            return planeFit;
        };

        for (let iter = 0; iter < maxIterations; iter++) {
            let improved = false;
            let directions = [
                [pitch + step, roll],
                [pitch - step, roll],
                [pitch, roll + step],
                [pitch, roll - step],
                [pitch + step, roll + step],
                [pitch + step, roll - step],
                [pitch - step, roll + step],
                [pitch - step, roll - step],
            ];

            let currentFit = evaluate(pitch, roll);
            for (const [p, r] of directions) {
                const f = evaluate(p, r);
                if (Math.abs(f.normal[2]) > Math.abs(currentFit.normal[2])) {
                    pitch = p;
                    roll = r;
                    currentFit = f;
                    improved = true;
                    break; // greedy move
                }
            }

            if (!improved) {
                if (step <= tolerance) break; // Converged
                step /= 2; // Refine search granularity
            }

            bestFit = currentFit;
        }

        return { pitch, roll, bestFit };
    }

    /**
     * Modern optimization using simultaneous parameter estimation.
     * More robust than the sequential coordinate descent methods.
     */
    optimizeCalibrationParameters(testData: number[][]): CalibrationParameters {
        if (testData.length < 4) {
            console.warn("Need at least 4 calibration pairs for robust optimization");
            return {
                pitch: this.height,
                roll: 0,
                height: this.height,
                returnHeight: this.estimatedReturnHeight
            };
        }

        // Try multiple starting points to avoid local minima
        const startingPoints = [
            { pitch: 20, roll: 0, height: 5, returnHeight: 0.8 },
            { pitch: 15, roll: -5, height: 4, returnHeight: 0.5 },
            { pitch: 25, roll: 5, height: 6, returnHeight: 1.0 },
            { pitch: 10, roll: 0, height: 3, returnHeight: 0.3 },
            { pitch: 30, roll: 0, height: 7, returnHeight: 0.6 }
        ];

        let bestResult: CalibrationParameters | null = null;
        let bestError = Infinity;

        for (const start of startingPoints) {
            try {
                const result = this.optimizeFromStartingPoint(testData, start);
                const error = this.evaluateCalibrationError(testData, result);

                if (error < bestError) {
                    bestError = error;
                    bestResult = result;
                }
            } catch (e) {
                console.warn(`Optimization failed for starting point ${JSON.stringify(start)}:`, e);
            }
        }

        if (!bestResult) {
            console.warn("All optimization attempts failed, using current parameters");
            return {
                pitch: this.height,
                roll: 0,
                height: this.height,
                returnHeight: this.estimatedReturnHeight
            };
        }

        return bestResult;
    }

    /**
     * Fit the device PLACEMENT (position translation + heading) together with the camera pose
     * (pitch/roll/height) from user-clicked image<->ground correspondences, by minimizing
     * pixel reprojection error (Huber-robustified so one bad click can't drag the fit) with
     * multi-start Nelder-Mead.
     *
     * This aligns the CAMERA to the GROUND/map — a placement problem — deliberately separate
     * from the radar↔camera fusion calibration (fusionOffset*): the fitted height/pitch/roll
     * estimate the same physical quantities through a different channel and should land close
     * to the radar-fitted values; a large disagreement means one of the two is wrong.
     *
     * The heading term rotates the ground points about the device (positive = the heading the
     * pairs were built with, plus the offset); the ground offset translates them, absorbing a
     * mis-placed device pin (see PoseFitResult.groundOffsetX for the frame conversion).
     * Evaluations run on a scratch translator, so calling this never disturbs `this`'s state.
     */
    fitPoseFromGroundPairs(
        pairs: GroundCalibrationPair[],
        start: { pitchDeg: number; rollDeg: number; heightM: number },
        options?: PoseFitOptions,
    ): PoseFitResult {
        if (pairs.length < 2) {
            throw new Error("Need at least 2 point pairs to fit the camera pose.");
        }

        const model = options?.cameraModel ?? this.cameraModel;
        const estimatedReturnHeight = options?.estimatedReturnHeight ?? 0.8;
        const fitHeading = options?.fitHeading ?? true;
        const fitPosition = options?.fitPosition ?? false;
        const fitFocalScale = options?.fitFocalScale ?? false;
        const regularize = options?.regularize ?? true;
        const scratch = new IntrinsicsTranslator();

        // A pair behind (or grazing) the camera can't produce a meaningful pixel — use a large
        // fixed penalty so the optimizer walks back toward poses that face the points.
        const BEHIND_PENALTY_PX = 640;

        const errorsFor = (pitchDeg: number, rollDeg: number, heightM: number, headingOffsetDeg: number, offsetX: number, offsetY: number, focalScale: number): number[] => {
            scratch.calculateFromParameters(false, heightM, pitchDeg, rollDeg, estimatedReturnHeight, model);
            if (focalScale !== 1) scratch.setFocalScale(focalScale);
            // Same rotation composition as facilityToDevice's heading term: an offset of δ on
            // the heading rotates the device-frame ground points by δ (CCW-positive); a device
            // position error is a constant device-frame translation on top.
            const offsetRad = (headingOffsetDeg * Math.PI) / 180;
            const cosO = Math.cos(offsetRad);
            const sinO = Math.sin(offsetRad);
            return pairs.map(({ pixel, ground }) => {
                const groundPoint = new Vector3({
                    x: ground.x * cosO - ground.y * sinO + offsetX,
                    y: ground.x * sinO + ground.y * cosO + offsetY,
                    z: 0,
                });
                const radar = scratch.groundToRadar(groundPoint);
                if (radar.y < 0.5) return BEHIND_PENALTY_PX;
                const projected = scratch.groundToImage(groundPoint);
                if (!Number.isFinite(projected.x) || !Number.isFinite(projected.y)) return BEHIND_PENALTY_PX;
                return Math.hypot(projected.x - pixel.x, projected.y - pixel.y);
            });
        };

        const huberDeltaPx = 20;
        const huber = (e: number) => (e < huberDeltaPx ? 0.5 * e * e : huberDeltaPx * (e - 0.5 * huberDeltaPx));

        // Soft priors keep the weakly-observable parameters honest: a handful of clicks can't
        // pin placement or focal hard, so moving them must BUY residual. One sigma of movement
        // costs as much as a uniform ~4.5 px residual. With regularize=false the priors are
        // off entirely and the fit is pure (Huber) reprojection least-squares.
        const HEADING_SIGMA_DEG = 5;
        const POSITION_SIGMA_M = 2;
        const FOCAL_SCALE_SIGMA = 0.1;
        const PRIOR_WEIGHT = regularize ? 10 : 0;

        const objective = (params: number[]): number => {
            const errs = errorsFor(params[0], params[1], params[2], params[3], params[4], params[5], params[6]);
            const dataCost = errs.reduce((acc, e) => acc + huber(e), 0) / errs.length;
            const headingPrior = fitHeading ? (params[3] / HEADING_SIGMA_DEG) ** 2 * PRIOR_WEIGHT : 0;
            const positionPrior = fitPosition ? ((params[4] * params[4] + params[5] * params[5]) / (POSITION_SIGMA_M * POSITION_SIGMA_M)) * PRIOR_WEIGHT : 0;
            const focalPrior = fitFocalScale ? ((params[6] - 1) / FOCAL_SCALE_SIGMA) ** 2 * PRIOR_WEIGHT : 0;
            return dataCost + headingPrior + positionPrior + focalPrior;
        };

        // Multi-start to dodge local minima: the caller's current pose plus perturbed variants
        // (nudges for each freed parameter — the simplex's own first step away from a
        // zero-valued parameter is tiny) and one generic mounting pose. The focal nudges are
        // the two competing dewarp-config interpretations (surface-scaled vs unscaled).
        // In exact mode the heading may start arbitrarily wrong (from-scratch placement seeds
        // heading 0), and the heading landscape has strong local minima — cover the compass
        // with quadrant starts so Nelder-Mead can find the true basin.
        const base = { pitchDeg: start.pitchDeg, rollDeg: start.rollDeg, heightM: start.heightM, headingOffsetDeg: 0, offsetX: 0, offsetY: 0, focalScale: 1 };
        const startingPoints = [
            base,
            { ...base, pitchDeg: start.pitchDeg + 5 },
            { ...base, pitchDeg: start.pitchDeg - 5 },
            { ...base, heightM: Math.max(1, start.heightM * 1.5) },
            { ...base, pitchDeg: 10, rollDeg: 0, heightM: 6 },
            ...(fitHeading ? [{ ...base, headingOffsetDeg: 5 }, { ...base, headingOffsetDeg: -5 }] : []),
            ...(fitHeading && !regularize
                ? [
                      { ...base, headingOffsetDeg: 45 },
                      { ...base, headingOffsetDeg: 90 },
                      { ...base, headingOffsetDeg: 135 },
                      { ...base, headingOffsetDeg: 180 },
                      { ...base, headingOffsetDeg: -45 },
                      { ...base, headingOffsetDeg: -90 },
                      { ...base, headingOffsetDeg: -135 },
                  ]
                : []),
            ...(fitPosition ? [{ ...base, offsetX: 3 }, { ...base, offsetX: -3 }, { ...base, offsetY: 3 }, { ...base, offsetY: -3 }] : []),
            ...(fitFocalScale ? [{ ...base, focalScale: 1.094 }, { ...base, focalScale: 0.914 }] : []),
        ];

        // Locked parameters get a degenerate bound — the simplex can never move them. The
        // regularized modes are small-correction modes (tight bounds); exact mode must be able
        // to reach any heading and a rough hand-placed position.
        const headingBound = fitHeading ? (regularize ? 15 : 180) : 0;
        const positionBound = fitPosition ? (regularize ? 6 : 12) : 0;
        const focalBounds = fitFocalScale ? [0.75, 1.35] : [1, 1];

        const nmOptions = {
            maxIterations: 800,
            tolerance: 1e-7,
            bounds: { min: [-10, -30, 0.5, -headingBound, -positionBound, -positionBound, focalBounds[0]], max: [60, 30, 30, headingBound, positionBound, positionBound, focalBounds[1]] },
        };

        let best: number[] | null = null;
        let bestValue = Infinity;
        for (const s of startingPoints) {
            const result = this.nelderMead(objective, [s.pitchDeg, s.rollDeg, s.heightM, s.headingOffsetDeg, s.offsetX, s.offsetY, s.focalScale], nmOptions);
            const value = objective(result);
            if (value < bestValue) {
                bestValue = value;
                best = result;
            }
        }

        // Nelder-Mead stalls in this problem's long flat valley (pitch/height/forward-position
        // trade off): the simplex collapses and reports convergence partway down. Restarting
        // with a fresh simplex at the incumbent keeps making progress, so polish until a
        // restart stops paying — otherwise users end up re-clicking Fit to do this manually.
        for (let i = 0; i < 8; i++) {
            const next = this.nelderMead(objective, best!, nmOptions);
            const nextValue = objective(next);
            if (nextValue < bestValue) {
                best = next;
            }
            const improved = bestValue - nextValue;
            bestValue = Math.min(bestValue, nextValue);
            if (improved < 1e-4) break;
        }

        const [pitchDeg, rollDeg, heightM, headingOffsetDeg, groundOffsetX, groundOffsetY, focalScale] = best!;
        const perPointErrorPx = errorsFor(pitchDeg, rollDeg, heightM, headingOffsetDeg, groundOffsetX, groundOffsetY, focalScale);
        const meanErrorPx = perPointErrorPx.reduce((a, b) => a + b, 0) / perPointErrorPx.length;
        return { pitchDeg, rollDeg, headingOffsetDeg, groundOffsetX, groundOffsetY, heightM, focalScale, meanErrorPx, perPointErrorPx };
    }

    private optimizeFromStartingPoint(testData: number[][], start: CalibrationParameters): CalibrationParameters {
        // Use Nelder-Mead optimization (derivative-free, robust)
        const objectiveFunction = (params: number[]): number => {
            const [pitch, roll, height, returnHeight] = params;
            return this.evaluateCalibrationError(testData, { pitch, roll, height, returnHeight });
        };

        const result = this.nelderMead(
            objectiveFunction,
            [start.pitch, start.roll, start.height, start.returnHeight],
            {
                maxIterations: 200,
                tolerance: 1e-6,
                bounds: {
                    min: [-30, -30, 0.5, 0],   // [pitch, roll, height, returnHeight]
                    max: [45, 30, 15, 1]       // returnHeight: 0-1 meters above ground
                }
            }
        );

        return {
            pitch: result[0],
            roll: result[1],
            height: result[2],
            returnHeight: result[3]
        };
    }

    private evaluateCalibrationError(testData: number[][], params: CalibrationParameters): number {
        // Temporarily store current state
        const originalHeight = this.height;
        const originalReturnHeight = this.estimatedReturnHeight;
        const originalRotation = this.rotation;
        const originalInvRotation = this.invRotation;

        try {
            // Apply test parameters
            this.calculateFromParameters(true, params.height, params.pitch, params.roll, params.returnHeight);

            let totalError = 0;
            let validPairs = 0;

            for (const [u, v, radarX, radarY] of testData) {
                try {
                    const groundFromImage = this.imageToGround(new Vector2({ x: u, y: v }));
                    const groundFromRadar = this.radarToGround(new Vector3({ x: radarX, y: radarY, z: 0 }));

                    const dx = groundFromImage.x - groundFromRadar.x;
                    const dy = groundFromImage.y - groundFromRadar.y;
                    const error = Math.sqrt(dx * dx + dy * dy);

                    // Use Huber loss to reduce influence of outliers
                    const huberDelta = 2.0; // threshold for Huber loss
                    const robustError = error < huberDelta ?
                        0.5 * error * error :
                        huberDelta * (error - 0.5 * huberDelta);

                    totalError += robustError;
                    validPairs++;
                } catch (e) {
                    // Skip invalid transformations
                    continue;
                }
            }

            return validPairs > 0 ? totalError / validPairs : Infinity;
        } finally {
            // Restore original state
            this.height = originalHeight;
            this.estimatedReturnHeight = originalReturnHeight;
            this.rotation = originalRotation;
            this.invRotation = originalInvRotation;
        }
    }

    /**
     * Simple Nelder-Mead implementation for derivative-free optimization
     */
    private nelderMead(
        objective: (x: number[]) => number,
        initial: number[],
        options: {
            maxIterations: number;
            tolerance: number;
            bounds?: { min: number[]; max: number[] };
        }
    ): number[] {
        const n = initial.length;
        const alpha = 1.0;    // reflection coefficient
        const gamma = 2.0;    // expansion coefficient
        const rho = 0.5;      // contraction coefficient
        const sigma = 0.5;    // shrink coefficient

        // Apply bounds
        const clampToBounds = (x: number[]): number[] => {
            if (!options.bounds) return x;
            return x.map((val, i) => Math.max(options.bounds!.min[i], Math.min(options.bounds!.max[i], val)));
        };

        // Initialize simplex
        let simplex: { point: number[]; value: number }[] = [];

        // Add initial point
        const initialClamped = clampToBounds(initial);
        simplex.push({ point: initialClamped, value: objective(initialClamped) });

        // Add n more points to form simplex
        for (let i = 0; i < n; i++) {
            const point = [...initialClamped];
            point[i] += point[i] * 0.05 + 0.1; // 5% perturbation + small constant
            const clampedPoint = clampToBounds(point);
            simplex.push({ point: clampedPoint, value: objective(clampedPoint) });
        }

        for (let iter = 0; iter < options.maxIterations; iter++) {
            // Sort by function value
            simplex.sort((a, b) => a.value - b.value);

            const best = simplex[0];
            const worst = simplex[n];
            const secondWorst = simplex[n - 1];

            // Check convergence
            const range = worst.value - best.value;
            if (range < options.tolerance) {
                break;
            }

            // Calculate centroid (excluding worst point)
            const centroid = new Array(n).fill(0);
            for (let i = 0; i < n; i++) {
                for (let j = 0; j < n; j++) {
                    centroid[j] += simplex[i].point[j];
                }
            }
            for (let j = 0; j < n; j++) {
                centroid[j] /= n;
            }

            // Reflection
            const reflected = centroid.map((c, i) => c + alpha * (c - worst.point[i]));
            const reflectedClamped = clampToBounds(reflected);
            const reflectedValue = objective(reflectedClamped);

            if (best.value <= reflectedValue && reflectedValue < secondWorst.value) {
                simplex[n] = { point: reflectedClamped, value: reflectedValue };
                continue;
            }

            // Expansion
            if (reflectedValue < best.value) {
                const expanded = centroid.map((c, i) => c + gamma * (reflectedClamped[i] - c));
                const expandedClamped = clampToBounds(expanded);
                const expandedValue = objective(expandedClamped);

                if (expandedValue < reflectedValue) {
                    simplex[n] = { point: expandedClamped, value: expandedValue };
                } else {
                    simplex[n] = { point: reflectedClamped, value: reflectedValue };
                }
                continue;
            }

            // Contraction
            if (reflectedValue >= secondWorst.value) {
                let contracted: number[];
                let contractedValue: number;

                if (reflectedValue < worst.value) {
                    // Outside contraction
                    contracted = centroid.map((c, i) => c + rho * (reflectedClamped[i] - c));
                    const contractedClamped = clampToBounds(contracted);
                    contractedValue = objective(contractedClamped);
                    contracted = contractedClamped;
                } else {
                    // Inside contraction
                    contracted = centroid.map((c, i) => c + rho * (worst.point[i] - c));
                    const contractedClamped = clampToBounds(contracted);
                    contractedValue = objective(contractedClamped);
                    contracted = contractedClamped;
                }

                if (contractedValue < Math.min(reflectedValue, worst.value)) {
                    simplex[n] = { point: contracted, value: contractedValue };
                    continue;
                }
            }

            // Shrink
            for (let i = 1; i <= n; i++) {
                const shrunk = best.point.map((b, j) => b + sigma * (simplex[i].point[j] - b));
                const shrunkClamped = clampToBounds(shrunk);
                simplex[i] = { point: shrunkClamped, value: objective(shrunkClamped) };
            }
        }

        simplex.sort((a, b) => a.value - b.value);
        return simplex[0].point;
    }

    private getPinhole(): PinholeParams {
        const p = PINHOLE_BY_MODEL[this.cameraModel];
        if (this.focalScale === 1) return p;
        return { fx: p.fx * this.focalScale, fy: p.fy * this.focalScale, cx: p.cx, cy: p.cy };
    }

    /**
     * Scale fx/fy by a factor (principal point unchanged). Diagnostic hook for the pose
     * fitter's lens-scale estimation — lets clicked pairs decide the true focal when the
     * hardcoded camera-model constants are in doubt. Reset to 1 by calculateFromParameters.
     */
    setFocalScale(scale: number): void {
        this.focalScale = scale;
        this.intrinsics = this.buildIntrinsicsMatrix();
        this.invertedIntrinsics = mat4.invert(mat4.create(), this.intrinsics);
    }

    /** Distortion applies only to the raw stream; nvdewarper output is distortion-free. */
    private getActiveDistortion(): { k1: number, k2: number, k3: number, p1: number, p2: number } | undefined {
        return this.cameraModel === "raw2716N" ? this.getDistortion2716N() : undefined;
    }

    private buildIntrinsicsMatrix(): mat4 {
        const p = this.getPinhole();
        return [
            p.fx, 0, 0, 0,
            0, p.fy, 0, 0,
            p.cx, p.cy, 1, 0,
            0, 0, 0, 1
        ];
    }

    private getDistortion2716N(): { k1: number, k2: number, k3: number, p1: number, p2: number } {
        // OpenCV calibration order is (k1, k2, p1, p2, k3) — k3 comes LAST, after the tangential
        // terms. These values must match the firmware's IntrinsicsTranslatorV2.LoadIntrinsics2716N
        // exactly, or browser overlays drift from what the device computes (worst at the corners).
        // Guarded by intrinsicTranslatorDistortion.test.ts (dashboard) + IntrinsicsDistortionParityTests (firmware).
        return {
            k1: -0.39083237,
            k2: 0.17261686,
            k3: -0.03969883,
            p1: 0.00044551,
            p2: 0.00221091
        };
    }

    standardDistortPoint(uv: Vector2): Vector2 {
        const distortion = this.getActiveDistortion();
        if (!distortion) return new Vector2({ x: uv.x, y: uv.y });

        var uvx = uv.x / 640 * 1920;
        var uvy = uv.y / 480 * 1080;

        const { fx, fy, cx, cy } = this.getPinhole();

        const x = (uvx - cx) / fx;
        const y = (uvy - cy) / fy;

        const r2 = x * x + y * y;

        // Radial term linearly extended past the polynomial's fold (see RADIAL_EXTENSION_START).
        const radial = radialScale(Math.sqrt(r2), distortion);

        const dx = 2 * distortion.p1 * x * y + distortion.p2 * (r2 + 2 * x * x);
        const dy = distortion.p1 * (r2 + 2 * y * y) + 2 * distortion.p2 * x * y;

        const xd = x * radial + dx;
        const yd = y * radial + dy;

        const distortedX = (xd * fx + cx) / 1920 * 640;
        const distortedY = (yd * fy + cy) / 1080 * 480;

        return new Vector2({ x: distortedX, y: distortedY });
    }

    standardUndistortPoint(uv: Vector2): Vector2 {
        const distortion = this.getActiveDistortion();
        if (!distortion) return new Vector2({ x: uv.x, y: uv.y });

        //Scale to 1920 x 1080 (calibration image size)
        var uvx = uv.x / 640 * 1920;
        var uvy = (uv.y / 480 * 1080);

        const { fx, fy, cx, cy } = this.getPinhole();

        var x = (uvx - cx) / fx;
        var y = (uvy - cy) / fy;

        // The naive fixed-point iteration (xu = (x - dx)/radial) diverged at frame-corner radii
        // (the raw polynomial folds there and those pixels had no preimage — vertices flew across
        // the screen). Instead, invert the extended monotonic radial curve directly — bisection on
        // the polynomial branch, closed-form on the linear extension — then apply damped
        // refinements for the small tangential terms. Mirrors IntrinsicsTranslatorV2.
        const ext = getRadialExtension(distortion);
        const rd = Math.sqrt(x * x + y * y);

        var xu: number, yu: number;
        if (rd < 1e-9) {
            xu = x;
            yu = y;
        } else {
            let rU: number;
            if (rd >= ext.startRd) {
                rU = RADIAL_EXTENSION_START + (rd - ext.startRd) / ext.slope;
            } else {
                let lo = 0;
                let hi = RADIAL_EXTENSION_START;
                for (let i = 0; i < 40; i++) {
                    const mid = (lo + hi) / 2;
                    if (mid * radialScale(mid, distortion) < rd) lo = mid;
                    else hi = mid;
                }
                rU = (lo + hi) / 2;
            }

            xu = x * (rU / rd);
            yu = y * (rU / rd);

            for (let i = 0; i < 16; i++) {
                const r2 = xu * xu + yu * yu;
                const radial = radialScale(Math.sqrt(r2), distortion);
                const dx = 2 * distortion.p1 * xu * yu + distortion.p2 * (r2 + 2 * xu * xu); //Tangential distortion factor x
                const dy = distortion.p1 * (r2 + 2 * yu * yu) + 2 * distortion.p2 * xu * yu; //Tangential distortion factor y

                xu += x - (xu * radial + dx);
                yu += y - (yu * radial + dy);
            }
        }

        //Scale back to 640x480 size
        return new Vector2({ x: (xu * fx + cx) / 1920 * 640, y: (yu * fy + cy) / 1080 * 480 });

    }

    //Takes 640x480 image coordinates and converts them to ground coordinates
    imageToGround(uv: Vector2, targetPlaneHeight?: number): Vector3 {
        var ud = this.standardUndistortPoint(uv);
        // Pixel coords stay y-down — the frame cx/cy were calibrated in (OpenCV convention,
        // same as FittingTool and firmware IntrinsicsTranslatorV2). The up-axis is handled by
        // the camera->device remap below, NOT by flipping v around the image height: cy sits
        // 47.7 px above the geometric center, so flipping v while keeping cy elevated every
        // ray by atan((1080 - 2*cy)/fy) ≈ 5° and mid-frame ranges came out roughly doubled.
        const imagePoint = vec4.fromValues(ud.x / 640 * 1920, ud.y / 480 * 1080, 1, 1);

        const rayCam = vec4.transformMat4(vec4.create(), imagePoint, this.invertedIntrinsics);
        // Camera (x right, y down, z fwd) -> device (x right, y fwd, z up): fwd = camZ, up = -camY.
        const rayWorld = vec3.transformMat4(vec3.create(), vec3.fromValues(rayCam[0], rayCam[2], -rayCam[1]), this.invRotation);

        vec3.normalize(rayWorld, rayWorld);

        //Intersect the estimated height plane by projecting the ray
        const rayOrigin = vec3.fromValues(0, 0, this.height);

        var planePoint = this.planePoint ?? vec3.fromValues(0, 0, targetPlaneHeight ?? this.estimatedReturnHeight);
        var planeNormal = this.planeNormal ?? vec3.fromValues(0, 0, 1);


        // t = (n · (p₀ - origin)) / (n · ray)
        const numerator = vec3.dot(planeNormal, vec3.subtract(vec3.create(), planePoint, rayOrigin));
        const denominator = vec3.dot(planeNormal, rayWorld);

        if (Math.abs(denominator) < 1e-6) {
            //no valid intersection
            return new Vector3({ x: 0, y: 0, z: 0 });
        }

        const t = numerator / denominator;
        const groundPoint = vec3.scaleAndAdd(vec3.create(), rayOrigin, rayWorld, t);

        return new Vector3({ x: groundPoint[0], y: groundPoint[1], z: groundPoint[2] });
    }

    imageToGroundClamped(uv: Vector2, targetPlaneHeight?: number, maxRange: number = 200): Vector3 {
        var ud = this.standardUndistortPoint(uv);
        // y-down pixels + camera->device remap; see imageToGround for why v must NOT be flipped.
        const imagePoint = vec4.fromValues(ud.x / 640 * 1920, ud.y / 480 * 1080, 1, 1);

        const rayCam = vec4.transformMat4(vec4.create(), imagePoint, this.invertedIntrinsics);
        const rayWorld = vec3.transformMat4(vec3.create(), vec3.fromValues(rayCam[0], rayCam[2], -rayCam[1]), this.invRotation);

        vec3.normalize(rayWorld, rayWorld);

        const rayOrigin = vec3.fromValues(0, 0, this.height);

        var planePoint = this.planePoint ?? vec3.fromValues(0, 0, targetPlaneHeight ?? this.estimatedReturnHeight);
        var planeNormal = this.planeNormal ?? vec3.fromValues(0, 0, 1);

        const numerator = vec3.dot(planeNormal, vec3.subtract(vec3.create(), planePoint, rayOrigin));
        const denominator = vec3.dot(planeNormal, rayWorld);

        const planeZ = planePoint[2];

        if (Math.abs(denominator) < 1e-6) {
            // Ray is nearly parallel to ground plane (at the horizon)
            const hx = rayWorld[0], hy = rayWorld[1];
            const hLen = Math.sqrt(hx * hx + hy * hy);
            if (hLen < 1e-6) return new Vector3({ x: 0, y: 0, z: 0 });
            return new Vector3({ x: hx / hLen * maxRange, y: hy / hLen * maxRange, z: planeZ });
        }

        const t = numerator / denominator;

        if (t <= 0) {
            const hx = rayWorld[0], hy = rayWorld[1];
            const hLen = Math.sqrt(hx * hx + hy * hy);
            if (hLen < 1e-6) return new Vector3({ x: 0, y: 0, z: 0 });
            return new Vector3({ x: hx / hLen * maxRange, y: hy / hLen * maxRange, z: planeZ });
        }

        const groundPoint = vec3.scaleAndAdd(vec3.create(), rayOrigin, rayWorld, t);

        return new Vector3({ x: groundPoint[0], y: groundPoint[1], z: groundPoint[2] });
    }

    //Takes 640x480 image coordinates and converts them to radar coordinates
    imageToRadar(uv: Vector2): Vector3 {
        return this.groundToRadar(this.imageToGround(uv));
    }

    radar2dToImage(radarCartesian: Vector3): Vector2 {

        const initial = vec3.fromValues(radarCartesian.x, radarCartesian.y, radarCartesian.z);
        if (this.planeNormal && this.planePoint) {
            var elevation = this.estimateElevation(radarCartesian.x, radarCartesian.y);
            initial[2] = elevation + this.height + this.estimatedReturnHeight;
            return this.radar3dToImage(this.groundToRadar(new Vector3({ x: initial[0], y: initial[1], z: initial[2] })));
        }

        const rotated = vec3.transformMat4(vec3.create(), initial, this.invRotation);
        rotated[2] = this.estimatedReturnHeight; // Flatten to ground level
        var result = this.radar3dToImage(this.groundToRadar(new Vector3({ x: rotated[0], y: rotated[1], z: rotated[2] })));
        return result;
    }

    radarToImage(radarCartesian: Vector3): Vector2 {
        if (this.is2dMode)
            return this.radar2dToImage(radarCartesian);
        else return this.radar3dToImage(radarCartesian);
    }

    radar3dToImage(radarCartesian: Vector3): Vector2 {
        // Device (x right, y fwd, z up) -> camera (x right, y down, z fwd): camY = -z, camZ = y.
        // Mirrors the ray construction in imageToGround (y-down pixels, no v flip).
        const camCoords = vec3.fromValues(radarCartesian.x, -radarCartesian.z, radarCartesian.y);
        const result = vec3.transformMat4(vec3.create(), camCoords, this.intrinsics);
        const retVal = new Vector2({ x: result[0] / result[2], y: result[1] / result[2] });
        retVal.x = 640 / 1920 * retVal.x;
        retVal.y = 480 / 1080 * retVal.y;
        return this.standardDistortPoint(retVal);
    }

    radarToGround(radarCartesian: Vector3): Vector3 {
        const input = vec3.fromValues(radarCartesian.x, radarCartesian.y, radarCartesian.z);
        var rotated = vec3.transformMat4(vec3.create(), input, this.invRotation);

        if (this.homography) {
            var result = this.homography.transform(rotated[0], rotated[1]);
            rotated[0] = result.x;
            rotated[1] = result.y;
        }

        const translated = vec3.fromValues(rotated[0], rotated[1], rotated[2] + this.height);

        return new Vector3({ x: translated[0], y: translated[1], z: translated[2] });
    }

    groundToRadar(groundCartesian: Vector3): Vector3 {
        const translated = vec3.fromValues(groundCartesian.x, groundCartesian.y, groundCartesian.z - this.height);
        const rotated = vec3.transformMat4(vec3.create(), translated, this.rotation);
        return new Vector3({ x: rotated[0], y: rotated[1], z: rotated[2] });
    }

    groundToImage(groundCartesian: Vector3): Vector2 {
        const result = this.radarToImage(this.groundToRadar(groundCartesian));
        return result;
    }
}
